import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  inject,
  signal,
  computed,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import {
  AddFormComponent,
  FormField,
  ActionButton,
  TableSection,
  TableAction,
} from '@shared/components/add-form/add-form.component';
import { TableColumn } from '@shared/components/data-table/data-table.component';
import { VendorService } from '../services/vendor.service';
import { VendorDTO } from '@app/api/vendor-service/model/vendorDTO';
import {
  VENDOR_FORM_FIELDS,
  VENDOR_ADD_HEADER_ACTIONS,
  VENDOR_EDIT_HEADER_ACTIONS,
  VENDOR_VIEW_HEADER_ACTIONS,
  VendorFormData,
} from './vendor-form.config';
import {
  CountriesService,
  Country,
} from '@app/feature/center-business/channel/warehouse/services/countries.service';
import {
  VietnamAddressService,
  VietnamProvince,
  VietnamWard,
} from '@app/feature/center-business/channel/warehouse/services/vietnam-address.service';
import { normalizeVietnamPhoneNumber } from '@shared/utils/phone-validator.util';

@Component({
  selector: 'app-vendor-form-wrapper',
  standalone: true,
  imports: [
    CommonModule,
    AddFormComponent,
    ToastModule,
    TranslateModule,
    ButtonModule,
    TooltipModule,
    AppButtonComponent,
  ],
  providers: [MessageService],
  templateUrl: './vendor-form-wrapper.component.html',
  styleUrl: './vendor-form-wrapper.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VendorFormWrapperComponent
  implements OnInit, OnDestroy, OnChanges
{
  private router = inject(Router);
  private messageService = inject(MessageService);
  private vendorService = inject(VendorService);
  private translateService = inject(TranslateService);
  private countriesService = inject(CountriesService);
  private vietnamAddressService = inject(VietnamAddressService);
  private cdr = inject(ChangeDetectorRef);
  private destroy$ = new Subject<void>();

  @Input() mode: 'add' | 'edit' | 'view' = 'add';
  @Input() vendorId?: string;
  @Output() formSubmit = new EventEmitter<any>();
  @Output() formCancel = new EventEmitter<void>();

  // Data signals
  loading = signal(false);
  submitting = signal(false);
  vendorData = signal<any>(null);
  vendorNumericId = signal<number | null>(null); // Store numeric ID for update operations
  vendorStatus = signal<string | null>(null); // Store vendor status

  // Form configuration
  formFields = signal<FormField[]>([]);
  headerActions = signal<ActionButton[]>([]);
  bankAccountsTable = signal<TableSection | null>(null);

  // Computed signal that always creates a new array reference when bankAccountsTable changes
  // This ensures Angular's @Input() setter detects the change even when only errorMessage updates
  tableSectionsForForm = computed(() => {
    // Read version to force re-evaluation when error changes
    const version = this.bankAccountsTableVersion();
    const table = this.bankAccountsTable();
    if (!table) {
      return [];
    }
    // Create a completely new object to ensure change detection works
    // This is critical for detecting errorMessage changes
    return [
      {
        title: table.title,
        name: table.name,
        show: table.show,
        columns: table.columns ? [...table.columns] : [],
        data: table.data ? [...table.data] : [],
        actions: table.actions ? [...table.actions] : [],
        hasCheckbox: table.hasCheckbox,
        hasSerialColumn: table.hasSerialColumn,
        selection: table.selection ? [...table.selection] : [],
        loading: table.loading,
        totalRecords: table.totalRecords,
        rows: table.rows,
        first: table.first,
        rowsPerPageOptions: table.rowsPerPageOptions,
        errorMessage: table.errorMessage, // This is the key property
        searchFormConfig: table.searchFormConfig,
        primaryButton: table.primaryButton,
        onSearch: table.onSearch,
        onReset: table.onReset,
        onprimaryButtonClick: table.onprimaryButtonClick,
        onPageChange: table.onPageChange,
        useAdvancedTable: table.useAdvancedTable,
        defaultColumns: table.defaultColumns,
        flexibleColumns: table.flexibleColumns,
        onColumnConfigClick: table.onColumnConfigClick,
      },
    ];
    // Version is read to force re-evaluation but not added to object
  });

  // Address data
  countries = signal<Country[]>([]);
  provinces = signal<VietnamProvince[]>([]);
  wards = signal<VietnamWard[]>([]);
  selectedProvinceId = signal<number | null>(null);

  // Computed values
  isEditMode = computed(() => this.mode === 'edit');
  isAddMode = computed(() => this.mode === 'add');
  isViewMode = computed(() => this.mode === 'view');

  // Reference to AddFormComponent to access form
  @ViewChild(AddFormComponent) addFormComponent?: AddFormComponent;

  // Loading state for tax validation
  validatingTax = signal(false);

  // Version counter to force computed signal re-evaluation
  private bankAccountsTableVersion = signal<number>(0);
  private skipCanDeactivate = false;

  // Flag to track if form is currently being validated
  // This prevents onFormValueChanges from clearing errors during validation
  private isValidating = false;

  // Check for duplicate bank accounts
  private checkForDuplicateBankAccounts(
    bankAccounts: Array<{
      id?: number;
      bankAccountName?: string;
      bankAccountNumber?: string;
      bankName?: string;
      swiftCode?: string;
    }>,
  ): boolean {
    for (let i = 0; i < bankAccounts.length; i++) {
      const current = bankAccounts[i];
      const currentName = (current.bankAccountName || '').trim();
      const currentNumber = (current.bankAccountNumber || '').trim();
      const currentBank = (current.bankName || '').trim();

      // Skip if required fields are empty
      if (!currentName || !currentNumber || !currentBank) {
        continue;
      }

      // Check against other bank accounts
      for (let j = i + 1; j < bankAccounts.length; j++) {
        const other = bankAccounts[j];
        const otherName = (other.bankAccountName || '').trim();
        const otherNumber = (other.bankAccountNumber || '').trim();
        const otherBank = (other.bankName || '').trim();

        // Skip if required fields are empty
        if (!otherName || !otherNumber || !otherBank) {
          continue;
        }

        // Check if bank account number and bank name match
        if (currentNumber === otherNumber && currentBank === otherBank) {
          return true; // Duplicate found
        }
      }
    }
    return false; // No duplicates
  }

  // Track which bank account rows have been validated (touched)
  private bankAccountRowsTouched = signal<Set<number>>(new Set());

  // Field error function for bank accounts table
  getBankAccountFieldError = (
    rowIndex: number,
    fieldName: string,
  ): string | null => {
    const table = this.bankAccountsTable();
    if (!table || !table.data || table.data.length === 0) {
      return null;
    }

    const currentRow = table.data[rowIndex];
    if (!currentRow) {
      return null;
    }

    // Only validate bank account fields
    const bankAccountFields = [
      'bankAccountName',
      'bankAccountNumber',
      'bankName',
      'swiftCode',
    ];
    if (!bankAccountFields.includes(fieldName)) {
      return null;
    }

    // Extract field values
    const bankAccountName = (currentRow.bankAccountName || '').trim();
    const bankAccountNumber = (currentRow.bankAccountNumber || '').trim();
    const bankName = (currentRow.bankName || '').trim();

    // Bank account fields are optional; only check duplicates when fully filled
    if (!bankAccountName || !bankAccountNumber || !bankName) {
      return null;
    }

    // Check for duplicates (compare bank account number and bank name only)
    const isDuplicate = table.data.some((ba: any, index: number) => {
      if (index === rowIndex) {
        return false; // Skip current row
      }

      const otherName = (ba.bankAccountName || '').trim();
      const otherNumber = (ba.bankAccountNumber || '').trim();
      const otherBank = (ba.bankName || '').trim();

      // If the other row doesn't have required fields, skip it
      if (!otherName || !otherNumber || !otherBank) {
        return false;
      }

      // Check if bank account number and bank name match
      return bankAccountNumber === otherNumber && bankName === otherBank;
    });

    if (isDuplicate) {
      // Return error message key for all duplicated fields
      return 'vendor.validation.bank_account_duplicated';
    }

    return null;
  };

  ngOnInit(): void {
    this.loadCountriesData();
    this.initializeForm();
    // Initialize field visibility for add mode
    if (this.isAddMode()) {
      this.updateFieldVisibility({});
    }
    if ((this.isEditMode() || this.isViewMode()) && this.vendorId) {
      this.loadVendorData();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Reload vendor data when vendorId input changes
    if (
      changes['vendorId'] &&
      !changes['vendorId'].firstChange &&
      (this.isEditMode() || this.isViewMode()) &&
      this.vendorId
    ) {
      this.loadVendorData();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadCountriesData(): void {
    this.countriesService
      .loadCountries()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (countries) => {
          this.countries.set(countries);
          this.updateFormFields();
          // Set default country value to Vietnam (VN) when in add mode
          if (this.isAddMode()) {
            setTimeout(() => {
              if (this.addFormComponent?.form) {
                const countryControl =
                  this.addFormComponent.form.get('country');
                if (countryControl && !countryControl.value) {
                  countryControl.setValue('VN');
                }
              }
            }, 100);
          }
        },
        error: (error) => {
          console.error('Error loading countries:', error);
          // Still update form fields even if countries fail to load
          this.updateFormFields();
        },
      });
  }

  private initializeForm(): void {
    // Set header actions based on mode
    if (this.isAddMode()) {
      this.headerActions.set(VENDOR_ADD_HEADER_ACTIONS);
    } else if (this.isViewMode()) {
      this.headerActions.set(VENDOR_VIEW_HEADER_ACTIONS);
    } else {
      this.headerActions.set(VENDOR_EDIT_HEADER_ACTIONS);
    }

    // Initialize form fields
    this.updateFormFields();

    // Initialize bank accounts table with empty data
    // For edit/view mode, table will be updated when data is loaded
    this.initializeBankAccountsTable([]);
  }

  // Removed updateBankAccountsTableError - using field-level errors instead

  private initializeBankAccountsTable(
    bankAccounts: Array<{
      id?: number;
      bankAccountName?: string;
      bankAccountNumber?: string;
      bankName?: string;
      swiftCode?: string;
    }>,
  ): void {
    // In view mode, use 'text' type to display as plain text instead of input fields
    const columnType = this.isViewMode() ? 'text' : 'inputText';

    const columns: TableColumn[] = [
      {
        prop: 'bankAccountName',
        label: 'vendor.form.fields.bank_account_name',
        type: columnType,
        required: false,
        width: 300,
        placeholder: this.isViewMode()
          ? undefined
          : this.translateService.instant(
              'vendor.form.placeholders.enter_bank_account_name',
            ),
      },
      {
        prop: 'bankAccountNumber',
        label: 'vendor.form.fields.bank_account_number',
        type: columnType,
        required: false,
        width: 300,
        placeholder: this.isViewMode()
          ? undefined
          : this.translateService.instant(
              'vendor.form.placeholders.enter_bank_account_number',
            ),
      },
      {
        prop: 'bankName',
        label: 'vendor.form.fields.bank_name',
        type: columnType,
        required: false,
        width: 300,
        placeholder: this.isViewMode()
          ? undefined
          : this.translateService.instant(
              'vendor.form.placeholders.enter_bank_name',
            ),
      },
      {
        prop: 'swiftCode',
        label: 'vendor.form.fields.swift_code',
        type: columnType,
        required: false,
        width: 250,
        placeholder: this.isViewMode()
          ? undefined
          : this.translateService.instant(
              'vendor.form.placeholders.enter_swift_code',
            ),
      },
      ...(this.isViewMode()
        ? []
        : [
            {
              prop: 'actions',
              label: this.translateService.instant(
                'vendor.form.fields.actions',
              ),
              type: 'actions',
              width: 120,
              align: 'center',
            } as TableColumn,
          ]),
    ];

    const actions: TableAction[] = this.isViewMode()
      ? []
      : [
          {
            label: 'common.actions.delete',
            action: 'deleteBankAccount',
            severity: 'danger',
          },
        ];

    // Create a new array reference to ensure change detection
    let bankAccountsData = bankAccounts ? [...bankAccounts] : [];

    // In add/edit mode, always ensure at least one empty row exists for validation
    // This allows field-level error messages to be displayed
    if (!this.isViewMode() && bankAccountsData.length === 0) {
      bankAccountsData = [
        {
          bankAccountName: '',
          bankAccountNumber: '',
          bankName: '',
          swiftCode: '',
        },
      ];
    }

    // Create a completely new object to ensure signal change detection
    const tableSection: TableSection = {
      title: 'vendor.form.sections.bank_information',
      name: 'bankAccounts',
      show: true,
      columns: [...columns], // Create new array reference
      data: bankAccountsData,
      actions: this.isViewMode()
        ? []
        : [
            {
              label: 'common.actions.delete',
              action: 'deleteSelected',
              severity: 'danger',
              outlined: true,
              disabled: true,
              icon: 'delete',
            },
            {
              label: 'common.actions.add',
              action: 'addBankAccount',
              severity: 'info',
              icon: 'add',
            },
          ],
      hasCheckbox: !this.isViewMode(),
      hasSerialColumn: false,
      selection: [],
      loading: false,
      // Remove section-level error message - use field-level errors instead
      errorMessage: null,
    };

    this.bankAccountsTable.set(tableSection);
  }

  private updateFormFields(): void {
    // Create deep copy of fields to avoid mutating the original config
    const fields = VENDOR_FORM_FIELDS.map((field) => ({ ...field }));

    // Update country options
    const countryField = fields.find((f) => f.controlName === 'country');
    if (countryField && this.countries().length > 0) {
      countryField.options = this.countries().map((c) => ({
        label: c.name_en || c.name,
        value: c.code,
      }));

      // Set default value to Vietnam (VN) when in add mode
      if (this.isAddMode()) {
        countryField.defaultValue = 'VN';
      }
    }

    // Set field disabled/readonly state based on mode
    fields.forEach((field) => {
      if (this.isViewMode()) {
        // In view mode, disable all fields
        field.disabled = true;
        field.readonly = true;
      } else {
        // In add/edit mode, ensure fields are enabled
        // Explicitly reset disabled/readonly to false
        field.disabled = false;
        field.readonly = false;

        // vendorCode should be readonly in edit mode unless status is DRAFT
        // Allow editing vendorCode only when status is DRAFT
        if (
          this.isEditMode() &&
          field.controlName === 'vendorCode' &&
          this.vendorStatus() !== 'DRAFT'
        ) {
          field.readonly = true;
        }
      }
    });

    this.formFields.set(fields);
  }

  /**
   * Check if error message indicates tax ID already exists
   * @param error - Error object from API
   * @returns true if tax ID already exists error
   */
  private isTaxIdAlreadyExistsError(error: any): boolean {
    const errorMessage = this.getErrorMessageString(error).toLowerCase();
    return errorMessage.includes('tax id has already existed');
  }

  private isVendorCodeAlreadyExistsError(error: any): boolean {
    const errorMessage = this.getErrorMessageString(error).toLowerCase();
    return (
      errorMessage.includes('vendorcode has already existed') ||
      errorMessage.includes('vendor code has already existed') ||
      errorMessage.includes('vendorcode already exists') ||
      errorMessage.includes('vendor code already exists')
    );
  }

  /**
   * Map known backend English error messages to i18n detail text.
   * Returns null when no known pattern matches.
   */
  private resolveTranslatedVendorErrorDetail(error: any): string | null {
    const message = this.getErrorMessageString(error);
    const lowerMessage = message.toLowerCase();
    const taxNumber = this.extractTaxNumberFromMessage(message);

    if (this.isTaxIdAlreadyExistsError(error)) {
      return this.translateService.instant(
        'vendor.messages.error.tax_id_already_exists',
      );
    }

    if (this.isVendorCodeAlreadyExistsError(error)) {
      return this.translateService.instant(
        'vendor.messages.error.vendor_code_already_exists',
      );
    }

    if (lowerMessage.includes('tax number shape')) {
      return this.translateService.instant(
        'vendor.messages.error.invalid_tax_number_shape',
        taxNumber ? { taxNumber } : undefined,
      );
    }

    if (lowerMessage.includes('invalid tax number')) {
      return this.translateService.instant(
        'vendor.messages.error.invalid_tax_number',
        taxNumber ? { taxNumber } : undefined,
      );
    }

    if (lowerMessage.includes('tax number can not be found')) {
      return this.translateService.instant(
        'vendor.messages.error.tax_number_not_found',
        taxNumber ? { taxNumber } : undefined,
      );
    }

    return null;
  }

  /**
   * Handle error and show appropriate toast message
   * @param error - Error object from API
   * @param defaultErrorKey - Default error translation key
   */
  private handleVendorError(error: any, defaultErrorKey: string): void {
    const translated = this.resolveTranslatedVendorErrorDetail(error);
    const rawMessage = this.getErrorMessageString(error);
    const detail =
      translated ||
      (rawMessage && !rawMessage.startsWith('{') ? rawMessage : null) ||
      this.translateService.instant(defaultErrorKey);

    this.messageService.add({
      severity: 'error',
      summary: this.translateService.instant('common.messages.error'),
      detail,
    });
  }

  private loadVendorData(): void {
    if (!this.vendorId) return;

    this.loading.set(true);
    this.vendorService.getVendorById(this.vendorId).subscribe({
      next: (vendor) => {
        if (vendor) {
          // Extract numeric ID from vendor.id if it's numeric, otherwise try to parse it
          const numericId = this.extractNumericId(vendor.id);
          if (numericId !== null) {
            this.vendorNumericId.set(numericId);
          }

          // Get bank accounts from API response (VendorDetailsVO has bankAccounts array)
          // Check if vendor has bankAccounts property (from VendorDetailsVO)
          const vendorAny = vendor as any;
          const bankAccounts = vendorAny.bankAccounts || [];

          // If no bankAccounts array but has old single bank fields, convert to array
          let bankAccountsArray: Array<{
            id?: number;
            bankAccountName?: string;
            bankAccountNumber?: string;
            bankName?: string;
            swiftCode?: string;
          }> = [];

          if (
            bankAccounts &&
            Array.isArray(bankAccounts) &&
            bankAccounts.length > 0
          ) {
            bankAccountsArray = bankAccounts.map((ba: any) => ({
              id: ba.id,
              bankAccountName: ba.bankAccountName || '',
              bankAccountNumber: ba.bankAccountNumber || '',
              bankName: ba.bankName || '',
              swiftCode: ba.swiftCode || '',
            }));
          } else if (
            vendor.bankAccountName ||
            vendor.bankAccountNumber ||
            vendor.bankName
          ) {
            // Fallback: convert old single bank fields to array
            // If old vendor has swiftCode at vendor level, add it to the first bank account
            const vendorAny = vendor as any;
            bankAccountsArray = [
              {
                bankAccountName: vendor.bankAccountName || '',
                bankAccountNumber: vendor.bankAccountNumber || '',
                bankName: vendor.bankName || '',
                swiftCode: vendorAny.swiftCode || '',
              },
            ];
          }

          // Map vendor data to form data structure
          const formData = {
            vendorCode: vendor.code || '',
            vendorName: vendor.name || '',
            vendorEmail: vendor.email || '',
            vendorPhone: vendor.phone || '',
            vendorAddress: vendor.address || '',
            description: vendor.description || '',
            country: this.mapCountryValue(vendor.country || ''),
            taxNumber: vendor.taxNumber || '',
            vendorType: this.mapVendorTypeValue(vendor.vendorType || ''),
            billingCurrency: vendor.billingCurrency || '',
            paymentTerm: this.mapPaymentTermValue(vendor.paymentTerm || ''),
            paymentMethod: this.mapPaymentMethodValue(
              vendor.paymentMethod || '',
            ),
            bankAccounts: bankAccountsArray,
            category: vendor.category || '',
            riskLevel: this.mapRiskLevelValue(vendor.riskLevel || ''),
            vendorPostingGroup: vendor.vendorPostingGroup || '',
            generalBusinessPostingGroup:
              vendor.generalBusinessPostingGroup || '',
            vatBusinessPostingGroup: vendor.vatBusinessPostingGroup || '',
            invoiceDiscCode: vendor.invoiceDiscCode || '',
          };
          this.vendorData.set(formData);
          // Store vendor status for field enablement logic
          this.vendorStatus.set(vendor.status || null);

          // Update form fields to reflect status-based field enablement
          this.updateFormFields();

          // Initialize bank accounts table with the loaded data
          this.initializeBankAccountsTable(bankAccountsArray);

          // Force change detection to ensure table updates
          this.cdr.markForCheck();
          setTimeout(() => {
            this.cdr.detectChanges();
          }, 0);
        }
        this.loading.set(false);
      },
      error: (error) => {
        console.error('Error loading vendor:', error);
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.messages.error'),
          detail: this.translateService.instant(
            'vendor.messages.error.failed_to_load_vendor',
          ),
        });
        this.loading.set(false);
      },
    });
  }

  // Map API display values to dropdown option values
  private mapVendorTypeValue(apiValue: string): string {
    const mapping: { [key: string]: string } = {
      Manufacturer: 'COMPANY',
      Company: 'COMPANY',
      Individual: 'INDIVIDUAL',
      Partnership: 'PARTNERSHIP',
    };
    return mapping[apiValue] || apiValue;
  }

  private mapPaymentTermValue(apiValue: string): string {
    const mapping: { [key: string]: string } = {
      'Net 30 Days': 'NET30',
      NET30: 'NET30',
      'Net 60 Days': 'NET60',
      NET60: 'NET60',
      Prepaid: 'PREPAID',
      PREPAID: 'PREPAID',
      COD: 'COD',
    };
    return mapping[apiValue] || apiValue;
  }

  private mapPaymentMethodValue(apiValue: string): string {
    const mapping: { [key: string]: string } = {
      'Bank Transfer': 'BANK_TRANSFER',
      BANK_TRANSFER: 'BANK_TRANSFER',
      COD: 'COD',
      Cash: 'CASH',
      CASH: 'CASH',
    };
    return mapping[apiValue] || apiValue;
  }

  private mapRiskLevelValue(apiValue: string): string {
    const mapping: { [key: string]: string } = {
      Low: 'LOW',
      LOW: 'LOW',
      Medium: 'MEDIUM',
      MEDIUM: 'MEDIUM',
      High: 'HIGH',
      HIGH: 'HIGH',
    };
    return mapping[apiValue] || apiValue;
  }

  private mapCountryValue(apiValue: string): string {
    // Map country name to country code (e.g., "Vietnam" -> "VN")
    const mapping: { [key: string]: string } = {
      Vietnam: 'VN',
      VN: 'VN',
    };
    return mapping[apiValue] || apiValue;
  }

  // Extract numeric ID from vendor ID (which could be numeric string or vendor code)
  private extractNumericId(vendorId: string): number | null {
    const numericId = Number(vendorId);
    if (!isNaN(numericId) && isFinite(numericId)) {
      return numericId;
    }
    return null;
  }

  onFormAction(event: { action: string; data?: any }): void {
    switch (event.action) {
      case 'save':
        this.onSave();
        break;
      case 'cancel':
        this.onCancel();
        break;
    }
  }

  canDeactivate(): boolean {
    if (this.isViewMode() || this.skipCanDeactivate || this.submitting()) {
      return true;
    }
    return !(this.addFormComponent?.form?.dirty ?? false);
  }

  onFormValueChanges(formData: any): void {
    // Store current form data
    // Merge bank accounts from table into form data
    const bankAccountsTable = this.bankAccountsTable();
    if (bankAccountsTable) {
      formData.bankAccounts = bankAccountsTable.data || [];
    }
    this.currentFormData = formData;
    // Update field visibility based on form values
    this.updateFieldVisibility(formData);

    // Only clear bank account touched rows if:
    // 1. NOT currently validating (to prevent clearing during validation)
    // 2. Bank accounts are actually valid
    if (
      !this.isValidating &&
      formData.bankAccounts &&
      formData.bankAccounts.length > 0
    ) {
      // Check if all bank accounts have required fields
      const allValid = formData.bankAccounts.every(
        (ba: any) =>
          ba.bankAccountName &&
          ba.bankAccountName.trim() !== '' &&
          ba.bankAccountNumber &&
          ba.bankAccountNumber.trim() !== '' &&
          ba.bankName &&
          ba.bankName.trim() !== '',
      );
      // Only clear touched rows if all bank accounts are valid
      // This ensures errors persist when other form fields are invalid
      if (allValid) {
        this.bankAccountRowsTouched.set(new Set());
      }
    }

    // Trigger change detection to update validation
    this.cdr.markForCheck();
  }

  onTableAction(event: { action: string; section?: string; data?: any }): void {
    const isBankAccountsSection =
      event.section === 'bankAccounts' ||
      event.section === 'vendor.form.sections.bank_information';

    if (isBankAccountsSection) {
      if (event.action === 'addBankAccount') {
        this.addBankAccount();
      } else if (
        event.action === 'deleteSelected' &&
        event.section === 'vendor.form.sections.bank_information'
      ) {
        this.deleteSelectedBankAccounts(event.data);
      } else if (event.action === 'deleteBankAccount' && event.data) {
        this.deleteBankAccount(event.data);
      } else if (event.action === 'selectionChange') {
        this.onBankAccountsSelectionChange(event.data);
      } else if (event.action === 'cellValueChange' && event.data) {
        // Handle cell value changes from table
        const cellEvent = event.data;
        const table = this.bankAccountsTable();
        if (table) {
          const updatedData = table.data.map((ba: any) =>
            ba === cellEvent.rowData
              ? { ...ba, [cellEvent.prop]: cellEvent.value }
              : ba,
          );
          this.bankAccountsTable.set({
            ...table,
            data: updatedData,
          });
          // Update current form data
          if (this.currentFormData) {
            this.currentFormData.bankAccounts = updatedData;
          }
          // Trigger change detection to update validation
          this.cdr.markForCheck();
        }
      } else if (event.action === 'cellInputChange' && event.data) {
        // Handle input changes from table (for real-time validation)
        const cellEvent = event.data;
        const table = this.bankAccountsTable();
        if (table) {
          const updatedData = table.data.map((ba: any) =>
            ba === cellEvent.rowData
              ? { ...ba, [cellEvent.prop]: cellEvent.value }
              : ba,
          );
          this.bankAccountsTable.set({
            ...table,
            data: updatedData,
          });
          // Update current form data
          if (this.currentFormData) {
            this.currentFormData.bankAccounts = updatedData;
          }
          // Trigger change detection to update validation
          this.cdr.markForCheck();
        }
      }
    }
  }

  private addBankAccount(): void {
    const table = this.bankAccountsTable();
    if (table) {
      const newAccount = {
        bankAccountName: '',
        bankAccountNumber: '',
        bankName: '',
        swiftCode: '',
      };
      this.bankAccountsTable.set({
        ...table,
        data: [...table.data, newAccount],
      });
      // Update current form data
      if (this.currentFormData) {
        this.currentFormData.bankAccounts = [
          ...(this.currentFormData.bankAccounts || []),
          newAccount,
        ];
      }
    }
  }

  private deleteBankAccount(account: any): void {
    const table = this.bankAccountsTable();
    if (table) {
      const updatedData = table.data.filter((ba: any) => ba !== account);
      this.bankAccountsTable.set({
        ...table,
        data: updatedData,
      });
      // Update current form data
      if (this.currentFormData) {
        this.currentFormData.bankAccounts = updatedData;
      }
    }
  }

  onDeleteBankAccountClick(rowData: any): void {
    this.deleteBankAccount(rowData);
  }

  private onBankAccountsSelectionChange(selection: any[]): void {
    const table = this.bankAccountsTable();
    if (table) {
      // Update selection and enable/disable delete button
      const updatedActions = table.actions?.map((action) => {
        if (action.action === 'deleteSelected') {
          return {
            ...action,
            disabled: !selection || selection.length === 0,
          };
        }
        return action;
      });

      this.bankAccountsTable.set({
        ...table,
        selection: selection || [],
        actions: updatedActions || table.actions,
      });
    }
  }

  private deleteSelectedBankAccounts(selectedAccounts: any[]): void {
    if (!selectedAccounts || selectedAccounts.length === 0) {
      return;
    }

    const table = this.bankAccountsTable();
    if (table) {
      // Filter out selected accounts using object reference comparison
      const updatedData = table.data.filter(
        (ba: any) => !selectedAccounts.includes(ba),
      );
      this.bankAccountsTable.set({
        ...table,
        data: updatedData,
        selection: [], // Clear selection after deletion
        actions: table.actions?.map((action) => {
          if (action.action === 'deleteSelected') {
            return { ...action, disabled: true };
          }
          return action;
        }),
      });
      // Update current form data
      if (this.currentFormData) {
        this.currentFormData.bankAccounts = updatedData;
      }
    }
  }

  private updateFieldVisibility(formData: any): void {
    const fields = [...this.formFields()];
    let hasChanges = false;

    fields.forEach((field) => {
      // Hide bank information fields when paymentMethod is COD
      // Note: swiftCode is now part of bank accounts table, not a form field
      if (field.section === 'vendor.form.sections.bank_information') {
        const shouldHide = formData.paymentMethod === 'COD';
        if (field.hidden !== shouldHide) {
          field.hidden = shouldHide;
          hasChanges = true;
        }
      }
    });

    if (hasChanges) {
      this.formFields.set([...fields]);
    }
  }

  private onSaveDraft(): void {
    const formData = this.currentFormData || {};
    if (!this.validateForm(formData, false)) {
      return;
    }

    this.submitting.set(true);
    const vendorDTO = this.mapFormDataToDTO(formData, 'DRAFT');

    if (this.isAddMode()) {
      this.vendorService.createVendor(vendorDTO).subscribe({
        next: (response) => {
          this.skipCanDeactivate = true;
          this.addFormComponent?.form?.markAsPristine();
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant(
              'vendor.messages.success.vendor_saved_draft',
            ),
          });
          this.submitting.set(false);
          // Delay navigation to allow toast to be visible
          setTimeout(() => {
            this.router.navigate(['/procurement/vendor']);
          }, 500);
        },
        error: (error) => {
          console.error('Error creating vendor:', error);
          this.handleVendorError(
            error,
            'vendor.messages.error.failed_to_create_vendor',
          );
          this.submitting.set(false);
        },
      });
    } else {
      // Edit mode - update vendor
      if (!this.vendorId) return;

      this.vendorService.updateVendor(this.vendorId, vendorDTO).subscribe({
        next: (response) => {
          this.skipCanDeactivate = true;
          this.addFormComponent?.form?.markAsPristine();
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant(
              'vendor.messages.success.vendor_updated',
            ),
          });
          this.submitting.set(false);
          // Delay navigation to allow toast to be visible
          setTimeout(() => {
            this.router.navigate(['/procurement/vendor']);
          }, 500);
        },
        error: (error) => {
          console.error('Error updating vendor:', error);
          this.handleVendorError(
            error,
            'vendor.messages.error.failed_to_update_vendor',
          );
          this.submitting.set(false);
        },
      });
    }
  }

  private onSubmitForReview(): void {
    const formData = this.currentFormData || {};
    if (!this.validateForm(formData, true)) {
      return;
    }

    this.submitting.set(true);
    const vendorDTO = this.mapFormDataToDTO(formData, 'REVIEWING');

    if (this.isAddMode()) {
      this.vendorService.createVendor(vendorDTO).subscribe({
        next: (response) => {
          // After creating, change status to REVIEWING
          if (response.id) {
            this.vendorService
              .changeStatus(response.id, 'REVIEWING')
              .subscribe({
                next: () => {
                  this.skipCanDeactivate = true;
                  this.addFormComponent?.form?.markAsPristine();
                  this.messageService.add({
                    severity: 'success',
                    summary: this.translateService.instant(
                      'common.messages.success',
                    ),
                    detail: this.translateService.instant(
                      'vendor.messages.success.vendor_submitted',
                    ),
                  });
                  this.submitting.set(false);
                  // Delay navigation to allow toast to be visible
                  setTimeout(() => {
                    this.router.navigate(['/procurement/vendor']);
                  }, 500);
                },
                error: (error) => {
                  console.error('Error changing status:', error);
                  this.submitting.set(false);
                },
              });
          } else {
            this.submitting.set(false);
          }
        },
        error: (error) => {
          console.error('Error creating vendor:', error);
          this.handleVendorError(
            error,
            'vendor.messages.error.failed_to_create_vendor',
          );
          this.submitting.set(false);
        },
      });
    } else {
      // Edit mode - update and change status
      if (!this.vendorId) return;

      this.vendorService.updateVendor(this.vendorId, vendorDTO).subscribe({
        next: (response) => {
          const numericId = parseInt(this.vendorId || '0');
          if (!isNaN(numericId)) {
            this.vendorService.changeStatus(numericId, 'REVIEWING').subscribe({
              next: () => {
                this.skipCanDeactivate = true;
                this.addFormComponent?.form?.markAsPristine();
                this.messageService.add({
                  severity: 'success',
                  summary: this.translateService.instant(
                    'common.messages.success',
                  ),
                  detail: this.translateService.instant(
                    'vendor.messages.success.vendor_submitted',
                  ),
                });
                this.submitting.set(false);
                // Delay navigation to allow toast to be visible
                setTimeout(() => {
                  this.router.navigate(['/procurement/vendor']);
                }, 500);
              },
              error: (error) => {
                console.error('Error changing status:', error);
                this.submitting.set(false);
              },
            });
          } else {
            this.submitting.set(false);
          }
        },
        error: (error) => {
          console.error('Error updating vendor:', error);
          this.handleVendorError(
            error,
            'vendor.messages.error.failed_to_update_vendor',
          );
          this.submitting.set(false);
        },
      });
    }
  }

  private onSave(): void {
    const formData = this.currentFormData || {};

    // Ensure we have the latest bank accounts data before validation
    const bankAccountsTable = this.bankAccountsTable();
    if (bankAccountsTable) {
      formData.bankAccounts = bankAccountsTable.data || [];
    }

    if (!this.validateForm(formData, true)) {
      return;
    }

    this.submitting.set(true);
    const vendorDTO = this.mapFormDataToDTO(formData);

    if (this.isAddMode()) {
      // Add mode - create new vendor
      this.vendorService.createVendor(vendorDTO).subscribe({
        next: (response) => {
          this.skipCanDeactivate = true;
          this.addFormComponent?.form?.markAsPristine();
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant(
              'vendor.messages.success.vendor_created',
            ),
          });
          this.submitting.set(false);
          // Delay navigation to allow toast to be visible
          setTimeout(() => {
            this.router.navigate(['/procurement/vendor']);
          }, 500);
        },
        error: (error) => {
          console.error('Error creating vendor:', error);
          this.handleVendorError(
            error,
            'vendor.messages.error.failed_to_create_vendor',
          );
          this.submitting.set(false);
        },
      });
    } else {
      // Edit mode - update existing vendor
      const numericId = this.vendorNumericId();
      if (!numericId) {
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.messages.error'),
          detail: this.translateService.instant(
            'vendor.messages.error.invalid_vendor_id',
          ),
        });
        this.submitting.set(false);
        return;
      }

      this.vendorService
        .updateVendor(numericId.toString(), vendorDTO)
        .subscribe({
          next: (response) => {
            this.skipCanDeactivate = true;
            this.addFormComponent?.form?.markAsPristine();
            this.messageService.add({
              severity: 'success',
              summary: this.translateService.instant('common.messages.success'),
              detail: this.translateService.instant(
                'vendor.messages.success.vendor_updated',
              ),
            });
            this.submitting.set(false);
            // Delay navigation to allow toast to be visible
            setTimeout(() => {
              this.router.navigate(['/procurement/vendor']);
            }, 500);
          },
          error: (error) => {
            console.error('Error updating vendor:', error);
            this.handleVendorError(
              error,
              'vendor.messages.error.failed_to_update_vendor',
            );
            this.submitting.set(false);
          },
        });
    }
  }

  private onCancel(): void {
    this.router.navigate(['/procurement/vendor']);
  }

  private currentFormData: any = {};

  // Validate vendor tax ID and auto-populate form fields
  onValidateTaxId(): void {
    if (!this.addFormComponent?.form) {
      return;
    }

    const taxNumber = this.addFormComponent.form.get('taxNumber')?.value;
    if (!taxNumber || taxNumber.trim() === '') {
      this.translateService
        .get('vendor.validation.tax_number_required')
        .pipe(takeUntil(this.destroy$))
        .subscribe((message) => {
          this.messageService.add({
            severity: 'warn',
            summary: this.translateService.instant('common.messages.warning'),
            detail: message,
          });
        });
      return;
    }

    this.validatingTax.set(true);
    this.updateButtonLoadingState(true);

    this.vendorService
      .validateVendorTaxId(taxNumber.trim())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this.validatingTax.set(false);
          this.updateButtonLoadingState(false);

          if (response) {
            // Auto-populate form fields
            const updates: any = {};

            // Map response to form fields
            if (response.name) {
              updates.vendorName = response.name;
            }
            if (response.address) {
              updates.vendorAddress = response.address;
            }
            if (response.shortName) {
              updates.vendorCode = response.shortName;
            }

            // Patch form values
            if (this.addFormComponent?.form) {
              this.addFormComponent.form.patchValue(updates);
            }

            this.messageService.add({
              severity: 'success',
              summary: this.translateService.instant('common.messages.success'),
              detail: this.translateService.instant(
                'vendor.messages.success.vendor_info_loaded',
              ),
            });
          }
        },
        error: (error) => {
          this.validatingTax.set(false);
          this.updateButtonLoadingState(false);
          console.error('Error validating tax ID:', error);
          this.handleTaxValidationError(error);
        },
      });
  }

  private getErrorMessageString(error: any): string {
    const rawMessage =
      error?.error?.message ||
      error?.message ||
      error?.error ||
      JSON.stringify(error || '');
    return typeof rawMessage === 'string' ? rawMessage : String(rawMessage);
  }

  private extractTaxNumberFromMessage(message: string): string | null {
    if (!message) {
      return null;
    }

    const parts = message.split(':');
    if (parts.length < 2) {
      return null;
    }

    const taxNumber = parts[parts.length - 1].trim();
    return taxNumber || null;
  }

  private handleTaxValidationError(error: any): void {
    const translated = this.resolveTranslatedVendorErrorDetail(error);
    this.messageService.add({
      severity: 'error',
      summary: this.translateService.instant('common.messages.error'),
      detail:
        translated ||
        this.translateService.instant(
          'vendor.messages.error.failed_to_validate_tax_id',
        ),
    });
  }

  private updateButtonLoadingState(loading: boolean): void {
    const button = document.querySelector(
      '.tax-validation-button button',
    ) as HTMLButtonElement;
    if (button) {
      const icon = button.querySelector('.p-button-icon i');
      if (icon) {
        icon.className = loading ? 'pi pi-spin pi-spinner' : 'pi pi-search';
      }
      button.disabled = loading;
    }
  }

  private validateForm(formData: any, requireAll: boolean): boolean {
    if (!this.addFormComponent?.form) {
      return false;
    }

    // Set validation flag to prevent onFormValueChanges from clearing errors
    this.isValidating = true;

    const form = this.addFormComponent.form;
    const requiredFields = [
      'vendorCode',
      'vendorName',
      'country',
      'vendorEmail',
      'vendorPhone',
      'vendorAddress',
      'taxNumber',
    ];

    const missingFields: string[] = [];
    let firstInvalidField: string | null = null;

    // Mark all controls as touched when form is invalid
    // This ensures error messages are visible
    const markAllControlsAsTouched = () => {
      Object.keys(form.controls).forEach((key) => {
        const control = form.get(key);
        if (control) {
          control.markAsTouched();
        }
      });
    };

    // Validate required form fields
    requiredFields.forEach((field) => {
      const control = form.get(field);
      if (control) {
        // Check both formData and control value to catch all cases
        const formDataValue = formData[field];
        const controlValue = control.value;
        const isEmpty =
          !formDataValue ||
          formDataValue === '' ||
          !controlValue ||
          controlValue === '' ||
          controlValue === null ||
          controlValue === undefined;

        if (isEmpty) {
          missingFields.push(field);
          // Mark as touched and set required error
          control.markAsTouched();
          if (!control.hasError('required')) {
            control.setErrors({ required: true });
            control.updateValueAndValidity();
          }
          // Track first invalid field for scrolling
          if (!firstInvalidField) {
            firstInvalidField = field;
          }
        } else {
          // Clear required error if field has value
          if (control.hasError('required') && (formDataValue || controlValue)) {
            const errors = { ...control.errors };
            delete errors['required'];
            control.setErrors(Object.keys(errors).length > 0 ? errors : null);
            control.updateValueAndValidity();
          }
        }
      }
    });

    // Bank account fields are optional; empty rows are ignored on save
    const bankAccountsTable = this.bankAccountsTable();
    const bankAccounts = bankAccountsTable?.data || formData.bankAccounts || [];

    if (missingFields.length > 0) {
      // Mark all controls as touched to show all errors
      markAllControlsAsTouched();

      // Force change detection to ensure errors are displayed
      this.cdr.markForCheck();
      this.cdr.detectChanges();

      // Scroll to first invalid field
      setTimeout(() => {
        if (firstInvalidField) {
          this.scrollToField(firstInvalidField);
        } else {
          console.warn('⚠️ [SCROLL] No target found for scrolling');
        }
      }, 250);

      // Reset validation flag AFTER a delay to prevent onFormValueChanges from clearing error
      setTimeout(() => {
        this.isValidating = false;
      }, 200);
      return false;
    }

    // Check for duplicate bank accounts (only among fully filled rows)
    if (bankAccounts.length > 1) {
      const hasDuplicates = this.checkForDuplicateBankAccounts(bankAccounts);
      if (hasDuplicates) {
        markAllControlsAsTouched();
        const allTouchedRows = new Set<number>();
        bankAccounts.forEach((_ba: any, index: number) => {
          allTouchedRows.add(index);
        });
        this.bankAccountRowsTouched.set(allTouchedRows);
        this.cdr.detectChanges();
        this.scrollToBankAccountsSection();
        this.isValidating = false;
        return false;
      }
    }

    // Reset validation flag if form is valid
    this.isValidating = false;
    return true;
  }

  /**
   * Scroll to a specific form field
   * Uses the same simple approach as scrollToBankAccountsSection
   */
  private scrollToField(fieldName: string): void {
    // Increase delay to ensure DOM is fully updated and ready for smooth scroll
    setTimeout(() => {
      // Use the same approach as scrollToBankAccountsSection - find scrollable container first
      let scrollContainer: HTMLElement | null = null;

      // Find the scrollable content container (same as bank accounts)
      scrollContainer = document.querySelector(
        '.flex-1.min-h-0.overflow-auto',
      ) as HTMLElement;

      if (!scrollContainer) {
        // Try finding by structure
        const form = document.querySelector('form[formGroup]');
        if (form && form.parentElement) {
          const parent = form.parentElement;
          if (
            parent.classList.contains('flex-1') &&
            parent.classList.contains('min-h-0')
          ) {
            const style = getComputedStyle(parent);
            if (
              style.overflow === 'auto' ||
              style.overflowY === 'auto' ||
              parent.scrollHeight > parent.clientHeight
            ) {
              scrollContainer = parent as HTMLElement;
            }
          }
        }
      }

      if (!scrollContainer) {
        // Try finding any scrollable element with flex-1 and min-h-0
        const candidates = document.querySelectorAll('.flex-1.min-h-0');
        for (const candidate of candidates) {
          const el = candidate as HTMLElement;
          const style = getComputedStyle(el);
          if (
            style.overflow === 'auto' ||
            style.overflowY === 'auto' ||
            el.scrollHeight > el.clientHeight
          ) {
            scrollContainer = el;
            break;
          }
        }
      }

      // Strategy 1: Try to find by field class (field-{controlName})
      // This is the most reliable since we add it directly to the form-field wrapper
      let elementToScroll: HTMLElement | null = null;
      const fieldClassSelector = `.field-${fieldName}`;
      elementToScroll = document.querySelector(
        fieldClassSelector,
      ) as HTMLElement;

      if (!elementToScroll) {
        return;
      }

      if (elementToScroll) {
        // Use scrollIntoView for smoother scrolling
        // It will automatically scroll the nearest scrollable ancestor (our container)
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            elementToScroll!.scrollIntoView({
              behavior: 'smooth',
              block: 'start', // Start of the viewport
              inline: 'nearest',
            });

            // If we have a scroll container, adjust for fixed header after scroll
            if (scrollContainer) {
              setTimeout(() => {
                const containerRect = scrollContainer.getBoundingClientRect();
                const elementRect = elementToScroll!.getBoundingClientRect();
                const headerHeight = 80; // Approximate header height
                const currentScrollTop = scrollContainer.scrollTop;

                // Check if element is behind the header and adjust if needed
                if (elementRect.top < containerRect.top + headerHeight) {
                  const adjustment =
                    headerHeight + 20 - (elementRect.top - containerRect.top);
                  scrollContainer.scrollTo({
                    top: currentScrollTop - adjustment,
                    behavior: 'smooth',
                  });
                }
              }, 100); // Small delay to let scrollIntoView start
            }
          });
        });

        // Try to focus the actual input element if possible
        // Find the form control element within the form-field
        const formControl = elementToScroll.querySelector(
          '[formControlName], [ng-reflect-form-control-name], input, select, textarea, [role="combobox"]',
        ) as HTMLElement;

        if (formControl) {
          // For PrimeNG components, try to find the actual input inside
          const focusableElement = formControl.querySelector(
            'input, select, textarea, [role="combobox"]',
          ) as HTMLElement;
          const elementToFocus = focusableElement || formControl;

          if (elementToFocus && elementToFocus.focus) {
            setTimeout(() => {
              try {
                elementToFocus.focus();
              } catch (e) {
                console.warn('⚠️ [SCROLL] Could not focus element:', e);
              }
            }, 200);
          }
        }
      }
    }, 150); // Increased delay for smoother scroll
  }

  /**
   * Scroll to bank accounts section
   */
  private scrollToBankAccountsSection(): void {
    setTimeout(() => {
      const section = document.querySelector('[data-section="bankAccounts"]');
      if (section) {
        section.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }, 150); // Increased delay for smoother scroll
  }

  private mapFormDataToDTO(
    formData: any,
    status?: 'DRAFT' | 'REVIEWING',
  ): VendorDTO {
    // Map bank accounts array
    const bankAccounts = (formData.bankAccounts || [])
      .map((ba: any) => ({
        id: ba.id,
        bankAccountName: ba.bankAccountName || undefined,
        bankAccountNumber: ba.bankAccountNumber || undefined,
        bankName: ba.bankName || undefined,
        swiftCode: ba.swiftCode || undefined,
      }))
      .filter(
        (ba: any) =>
          ba.bankAccountName && ba.bankAccountNumber && ba.bankName,
      );

    const dto: VendorDTO = {
      vendorCode: formData.vendorCode || '',
      vendorName: formData.vendorName || '',
      vendorEmail: formData.vendorEmail || '',
      // Normalize VN phone when user input drops leading "0" (e.g. "708..." -> "070...")
      vendorPhone: normalizeVietnamPhoneNumber(formData.vendorPhone || ''),
      vendorAddress: formData.vendorAddress || '',
      description: formData.description || undefined,
      country: formData.country || undefined,
      taxNumber: formData.taxNumber || undefined,
      vendorType: formData.vendorType || undefined,
      billingCurrency: formData.billingCurrency || undefined,
      paymentTerm: formData.paymentTerm || undefined,
      paymentMethod: formData.paymentMethod || undefined,
      bankAccounts: bankAccounts.length > 0 ? bankAccounts : undefined,
      category: formData.category || undefined,
      riskLevel: formData.riskLevel || undefined,
      vendorPostingGroup: formData.vendorPostingGroup || undefined,
      generalBusinessPostingGroup:
        formData.generalBusinessPostingGroup || undefined,
      vatBusinessPostingGroup: formData.vatBusinessPostingGroup || undefined,
      invoiceDiscCode: formData.invoiceDiscCode || undefined,
    };

    // Remove undefined values
    Object.keys(dto).forEach((key) => {
      if (dto[key as keyof VendorDTO] === undefined) {
        delete dto[key as keyof VendorDTO];
      }
    });

    return dto;
  }
}
